import * as THREE from '/assets/scripts/three.module.js';

const FAN_MARKER_COLOR = 0xa78bfa;
const RADIATOR_MARKER_COLOR = 0x34d399;

function finite(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function normalizedZone(value = '') {
  const raw=String(value||'').trim().toLowerCase().replace(/[_-]+/g,' ');
  if (raw.includes('motherboard side') || raw.includes('mobo side')) return 'motherboard_side';
  if (raw.includes('gpu side') || raw.includes('graphics card side')) return 'gpu_side';
  if (raw.includes('top')) return 'top';
  if (raw.includes('bottom') || raw.includes('lower')) return 'bottom';
  if (raw.includes('front')) return 'front';
  if (raw.includes('rear') || raw.includes('back')) return 'rear';
  if (raw.includes('left')) return 'side_left';
  if (raw.includes('right')) return 'side_right';
  if (raw.includes('side')) return 'side';
  return '';
}

export function normalizeCatalogSpatialHints(raw) {
  if (!raw || typeof raw !== 'object') return { schema:0, fans:[], radiators:[], parse:{} };
  const fans=Array.isArray(raw.fans) ? raw.fans.map((hint,index)=>{
    const zone=normalizedZone(hint?.zone);
    const sizes=[...(hint?.sizes||[])].map(Number).filter(v=>Number.isFinite(v)&&v>0);
    const counts={};
    for(const [key,value] of Object.entries(hint?.counts||{})){
      const size=String(Math.round(Number(key)||0));
      const count=Math.max(0,Math.round(Number(value)||0));
      if(size!=='0'&&count>0)counts[size]=count;
    }
    const maxFanThickness={};
    for(const [key,value] of Object.entries(hint?.maxFanThickness||{})){
      const size=String(Math.round(Number(key)||0));
      const thickness=finite(value,0);
      if(size!=='0'&&thickness>0)maxFanThickness[size]=thickness;
    }
    return {
      ...hint,
      id:String(hint?.id || `fan_hint_${index}`),
      zone,
      sizes:[...new Set(sizes)],
      counts,
      maxFanThickness,
      sources:Array.isArray(hint?.sources)?[...hint.sources]:[],
      placementConfidence:String(hint?.placementConfidence || (zone==='side'?'ambiguous_side':'explicit_zone')),
      // A bare `side` note is capability evidence only. Never allow a stale/older backend
      // payload to promote it into positional authority; only explicit left/right, motherboard
      // side or GPU side hints may create snap targets.
      snapEligible:zone==='side' ? false : (hint?.snapEligible===undefined ? true : !!hint.snapEligible)
    };
  }).filter(hint=>hint.zone&&hint.sizes.length) : [];

  const radiators=Array.isArray(raw.radiators) ? raw.radiators.map((hint,index)=>{
    const counts={};
    for(const [key,value] of Object.entries(hint?.counts||{})){
      const size=String(Math.round(Number(key)||0));
      const count=Math.max(0,Math.round(Number(value)||0));
      if(size!=='0'&&count>0)counts[size]=count;
    }
    return {
      ...hint,
      id:String(hint?.id || `radiator_hint_${index}`),
      zone:normalizedZone(hint?.zone),
      sizes:[...new Set([...(hint?.sizes||[])].map(Number).filter(v=>Number.isFinite(v)&&v>0))],
      counts,
      maxStack:finite(hint?.maxStack,0),
      maxRadiatorThickness:finite(hint?.maxRadiatorThickness,0),
      sources:Array.isArray(hint?.sources)?[...hint.sources]:[],
      conditions:Array.isArray(hint?.conditions)?[...hint.conditions]:[],
      placementConfidence:String(hint?.placementConfidence || (normalizedZone(hint?.zone)==='side'?'ambiguous_side':'explicit_zone')),
      snapEligible:normalizedZone(hint?.zone)==='side' ? false : (hint?.snapEligible===undefined ? true : !!hint.snapEligible)
    };
  }).filter(hint=>hint.zone&&hint.sizes.length) : [];

  return {
    schema:finite(raw.schema,0),
    fans,
    radiators,
    parse:raw.parse&&typeof raw.parse==='object'?{...raw.parse}:{},
    unparsedClauses:raw.unparsedClauses&&typeof raw.unparsedClauses==='object'?{...raw.unparsedClauses}:{}
  };
}

function caseDims(caseBox) {
  const specs=caseBox?.nativeSpecs || caseBox?.catalogRecord || caseBox || {};
  return {
    l:finite(specs.l,finite(caseBox?.catalogRecord?.l,0)),
    w:finite(specs.w,finite(caseBox?.catalogRecord?.w,0)),
    h:finite(specs.h,finite(caseBox?.catalogRecord?.h,0))
  };
}

function isVerticalReferenceSource(source) {
  const record=source?.catalogRecord || source || {};
  const state=source?.spatialState || {};
  const physicalId=String(
    state?.physicalLayoutId
    || record?.physicalLayout?.layoutId
    || record?.physicalLayoutId
    || ''
  ).toLowerCase();
  if(physicalId==='vertical_reference')return true;
  const family=String(record?.style || record?.layout || state?.layoutFamily || '').toLowerCase();
  const dims=caseDims(source);
  return family.includes('reference') && dims.h>dims.l;
}

function projectedVerticalReferenceZone(zone) {
  const normalized=normalizedZone(zone);
  const canonicalToPhysical={
    top:'rear',
    bottom:'front',
    front:'top',
    rear:'bottom'
  };
  return canonicalToPhysical[normalized] || normalized;
}

function relativeSideFace(caseBox,activeBoxes=[],kind='motherboard') {
  const component=(activeBoxes||[]).find(item=>item?.isComponent && item.trackType===kind) || null;
  if(component && caseBox?.worldToLocal && component?.getWorldPosition){
    const local=caseBox.worldToLocal(component.getWorldPosition(new THREE.Vector3()));
    if(Math.abs(local.x)>1)return local.x>=0?'side_right':'side_left';
  }
  const candidate=caseBox?.spatialPlan?.candidates?.[kind]?.[0] || null;
  const x=Number(candidate?.pos?.x ?? candidate?.position?.[0]);
  if(Number.isFinite(x) && Math.abs(x)>1)return x>=0?'side_right':'side_left';
  return '';
}

function resolveHardwareRelativeZone(zone,caseBox,activeBoxes=[]) {
  if(zone==='motherboard_side')return relativeSideFace(caseBox,activeBoxes,'motherboard');
  if(zone==='gpu_side')return relativeSideFace(caseBox,activeBoxes,'gpu');
  return zone;
}

function zoneFaces(zone,{ambiguousSideBoth=false}={}) {
  // A plain source-note `side` describes one logical mounting surface, not both side
  // panels. Treating it as two independent faces doubled declared capacity and let
  // repeated fan adds migrate onto the opposite/exterior-looking panel. Keep one
  // canonical side for actual markers/snap slots; frame-fit scoring may still test both.
  if(zone==='side')return ambiguousSideBoth?['side_right','side_left']:['side_right'];
  return zone?[zone]:[];
}

function localFaceQuaternion(face) {
  const q=new THREE.Quaternion();
  if(face==='side_right'||face==='side_left') q.setFromEuler(new THREE.Euler(0,0,Math.PI/2));
  else if(face==='front'||face==='rear') q.setFromEuler(new THREE.Euler(Math.PI/2,0,0));
  return q;
}

function facePlaneAxes(face,dims) {
  if(face==='top'||face==='bottom') return [{axis:'z',span:dims.l},{axis:'x',span:dims.w}];
  if(face==='side_right'||face==='side_left') return [{axis:'z',span:dims.l},{axis:'y',span:dims.h}];
  return [{axis:'y',span:dims.h},{axis:'x',span:dims.w}];
}

function preferredRowAxis(face,dims,count,frame) {
  const axes=facePlaneAxes(face,dims);
  const need=count*frame;
  const fitting=axes
    .filter((item,index)=>item.span+1>=need && axes[1-index]?.span+1>=frame)
    .sort((a,b)=>b.span-a.span);
  return fitting[0]?.axis || null;
}

function fanHintFitsFace(face,dims,hint) {
  for(const size of hint?.sizes||[]){
    const count=Math.max(0,Math.round(Number(hint?.counts?.[String(Math.round(size))])||0));
    if(count<=0)continue;
    if(!preferredRowAxis(face,dims,count,size))return false;
  }
  return true;
}

function radiatorOrientationForFace(face,dims,rad) {
  const axes=facePlaneAxes(face,dims).map(item=>item.span);
  if(axes.length<2)return null;
  const a=finite(rad?.l,0), b=finite(rad?.w,0);
  if(a<=0||b<=0)return null;
  if(axes[0]+1>=a && axes[1]+1>=b)return 'default';
  if(axes[0]+1>=b && axes[1]+1>=a)return 'swapped';
  return null;
}

function radiatorFitsFace(face,dims,rad) {
  return !!radiatorOrientationForFace(face,dims,rad);
}

function hintFitsZone(kind,zone,dims,hint,sizeOverride=0) {
  const faces=zoneFaces(zone,{ambiguousSideBoth:true});
  if(!faces.length)return false;
  if(kind==='fan')return faces.some(face=>fanHintFitsFace(face,dims,hint));
  const size=finite(sizeOverride,0) || Math.max(...(hint?.sizes||[0]));
  const rad=nominalRadiatorGeometry(size);
  return !!rad && faces.some(face=>radiatorFitsFace(face,dims,rad));
}

function evidenceFrameScore(source,evidence,frame='physical') {
  const dims=caseDims(source);
  let score=0;
  const zoneFor=zone=>frame==='reference_canonical_projected'?projectedVerticalReferenceZone(zone):normalizedZone(zone);
  for(const hint of evidence?.fans||[])if(hintFitsZone('fan',zoneFor(hint.zone),dims,hint))score+=1;
  for(const hint of evidence?.radiators||[])if(hintFitsZone('radiator',zoneFor(hint.zone),dims,hint))score+=1;
  return score;
}

export function resolveSpatialEvidenceFrame(source,evidenceInput=null) {
  const evidence=evidenceInput || normalizeCatalogSpatialHints(source?.catalogRecord?.spatialHints || source?.spatialHints);
  if(!isVerticalReferenceSource(source))return 'physical';
  const physicalScore=evidenceFrameScore(source,evidence,'physical');
  const projectedScore=evidenceFrameScore(source,evidence,'reference_canonical_projected');
  return projectedScore>physicalScore ? 'reference_canonical_projected' : 'physical';
}

function resolvedEvidenceZone(zone,frame) {
  return frame==='reference_canonical_projected' ? projectedVerticalReferenceZone(zone) : normalizedZone(zone);
}

function localFacePosition(face,dims,thickness) {
  const halfT=Math.max(0.5,thickness/2);
  if(face==='top')return new THREE.Vector3(0,dims.h/2-halfT,0);
  if(face==='bottom')return new THREE.Vector3(0,-dims.h/2+halfT,0);
  if(face==='side_right')return new THREE.Vector3(dims.w/2-halfT,0,0);
  if(face==='side_left')return new THREE.Vector3(-dims.w/2+halfT,0,0);
  if(face==='front')return new THREE.Vector3(0,0,dims.l/2-halfT);
  if(face==='rear')return new THREE.Vector3(0,0,-dims.l/2+halfT);
  return new THREE.Vector3();
}

function rowOffsets(count,frame) {
  if(count<=1)return [0];
  const start=-((count-1)*frame)/2;
  return Array.from({length:count},(_,index)=>start+(index*frame));
}

function caseWorldTarget(caseBox, localPosition, localQuaternion) {
  caseBox.updateMatrixWorld(true);
  const worldPosition=localPosition.clone().applyMatrix4(caseBox.matrixWorld);
  const caseWorldQuaternion=caseBox.getWorldQuaternion(new THREE.Quaternion());
  const worldQuaternion=caseWorldQuaternion.multiply(localQuaternion.clone());
  return { worldPosition, worldQuaternion };
}

function fanTargetWorldBox(frame,thickness,worldPosition,worldQuaternion) {
  const hx=Math.max(0.5,finite(frame,0)/2);
  const hy=Math.max(0.5,finite(thickness,0)/2);
  const hz=hx;
  const points=[];
  for(const x of [-hx,hx])for(const y of [-hy,hy])for(const z of [-hz,hz]){
    const point=new THREE.Vector3(x,y,z);
    point.applyQuaternion?.(worldQuaternion);
    point.add?.(worldPosition);
    points.push(point);
  }
  return new THREE.Box3().setFromPoints(points);
}

function cleanWorldBox(obj) {
  const targetBox=new THREE.Box3();
  let found=false;
  obj?.updateMatrixWorld?.(true);
  const visit=(node,isRoot=false)=>{
    if(!isRoot&&node?.isComponent)return;
    if(node?.isMesh&&node.type!=='BoxHelper'&&node.type!=='LineSegments'&&!node.isFoot){
      node.geometry?.computeBoundingBox?.();
      const local=node.geometry?.boundingBox?.clone?.();
      if(local){
        local.applyMatrix4?.(node.matrixWorld);
        if(!found){targetBox.copy(local);found=true;}else targetBox.union(local);
      }
    }
    for(const child of node?.children||[])visit(child,false);
  };
  visit(obj,true);
  if(!found&&targetBox.setFromObject)targetBox.setFromObject(obj);
  return targetBox;
}

function meaningfulBoxOverlap(a,b,inset=0.65) {
  const aa=a.clone();const bb=b.clone();
  const aSize=aa.getSize(new THREE.Vector3());
  const bSize=bb.getSize(new THREE.Vector3());
  const safeInset=Math.max(0,Math.min(inset,
    Math.min(aSize.x,aSize.y,aSize.z,bSize.x,bSize.y,bSize.z)/4));
  if(safeInset>0){aa.expandByScalar(-safeInset);bb.expandByScalar(-safeInset);}
  return aa.intersectsBox(bb);
}

export function caseFanTargetBlockers(target, fanMesh, activeBoxes = []) {
  if(!target?.worldPosition||!target?.worldQuaternion||!fanMesh)return[];
  const frame=finite(fanMesh?.nativeSpecs?.l || fanMesh?.nativeSpecs?.w || target?.frame,0);
  const thickness=finite(fanMesh?.nativeSpecs?.h,25);
  if(frame<=0||thickness<=0)return[];
  const proposed=fanTargetWorldBox(frame,thickness,target.worldPosition,target.worldQuaternion);
  const blockers=[];
  for(const item of activeBoxes||[]){
    if(!item?.isComponent||item===fanMesh||item.uid===fanMesh?.uid)continue;
    if(item?.runtimeContainment?.status==='parked')continue;
    // Auxiliary tube/block geometry is deliberately advisory and is not stored as an
    // activeBoxes component. Real motherboard/PSU/GPU/radiator/fan envelopes do block a
    // physical case-fan mount.
    const box=cleanWorldBox(item);
    if(!box||box.isEmpty?.())continue;
    if(meaningfulBoxOverlap(proposed,box))blockers.push(item);
  }
  return blockers;
}

function occupiedCaseFanMounts(activeBoxes, caseUid, excludeUid='') {
  return new Set((activeBoxes||[])
    .filter(item=>item?.trackType==='fan'&&item.uid!==excludeUid&&item.attachedToCaseUid===caseUid&&item.caseFanMountId)
    .map(item=>String(item.caseFanMountId)));
}

export function buildCaseFanSnapTargets(caseBox, fanMesh, activeBoxes = [], { allowSizeMismatch = false, includeBlocked = false } = {}) {
  if(!caseBox?.isCase||!fanMesh)return[];
  const evidence=normalizeCatalogSpatialHints(caseBox.catalogRecord?.spatialHints);
  if(!evidence.fans.length)return[];
  const dims=caseDims(caseBox);
  const evidenceFrame=resolveSpatialEvidenceFrame(caseBox,evidence);
  const frame=finite(fanMesh?.nativeSpecs?.l || fanMesh?.nativeSpecs?.w,0);
  const thickness=finite(fanMesh?.nativeSpecs?.h,25);
  if(frame<=0)return[];
  const occupied=occupiedCaseFanMounts(activeBoxes,caseBox.uid,fanMesh.uid);
  const current=fanMesh.getWorldPosition(new THREE.Vector3());
  const targets=[];

  for(const hint of evidence.fans){
    if(hint.snapEligible===false)continue;
    const sizes=hint.sizes||[];
    const matchedSize=sizes.find(size=>Math.abs(size-frame)<=1.5) || (allowSizeMismatch?sizes[0]:null);
    if(!matchedSize)continue;
    const count=Math.max(0,Math.round(Number(hint.counts?.[String(Math.round(matchedSize))])||0));
    if(count<=0)continue;
    const maxThickness=finite(hint.maxFanThickness?.[String(Math.round(matchedSize))],0);
    if(maxThickness>0 && thickness>maxThickness+0.25)continue;

    const resolvedZone=resolveHardwareRelativeZone(resolvedEvidenceZone(hint.zone,evidenceFrame),caseBox,activeBoxes);
    if(!resolvedZone)continue;
    const logicalTargets=new Map();
    // A generic `side` note is one logical surface whose exact left/right panel is unknown.
    // Test both physical faces as alternatives, but keep only ONE target per declared slot so
    // the source count cannot double. Hardware collisions naturally choose the usable side.
    for(const face of zoneFaces(resolvedZone,{ambiguousSideBoth:resolvedZone==='side'})){
      const axis=preferredRowAxis(face,dims,count,matchedSize);
      if(!axis)continue;
      const base=localFacePosition(face,dims,thickness);
      const localQuaternion=localFaceQuaternion(face);
      for(const [slot,offset] of rowOffsets(count,matchedSize).entries()){
        const localPosition=base.clone();
        localPosition[axis]=offset;
        const id=`casefan:${hint.id}:${Math.round(matchedSize)}:${slot}`;
        if(occupied.has(id))continue;
        const {worldPosition,worldQuaternion}=caseWorldTarget(caseBox,localPosition,localQuaternion);
        const target={
          id,
          source:'catalog_spatial_hint',
          caseUid:caseBox.uid,
          hintId:hint.id,
          zone:resolvedZone,
          sourceZone:hint.zone,
          evidenceFrame,
          face,
          frame:matchedSize,
          slot,
          count,
          maxThickness:maxThickness||null,
          sources:[...(hint.sources||[])],
          worldPosition,
          worldQuaternion,
          distance:worldPosition.distanceTo(current)
        };
        const blockers=caseFanTargetBlockers(target,fanMesh,activeBoxes);
        if(blockers.length && !includeBlocked)continue;
        target.blockers=blockers;
        const prior=logicalTargets.get(id);
        if(!prior||target.distance<prior.distance)logicalTargets.set(id,target);
      }
    }
    targets.push(...logicalTargets.values());
  }
  return targets.sort((a,b)=>a.distance-b.distance);
}

export function placeFanOnCaseSpatialTarget(fanMesh, target, caseBox, scene, activeBoxes = []) {
  if(!fanMesh||!target||!caseBox||!scene)return false;
  if(caseFanTargetBlockers(target,fanMesh,activeBoxes).length)return false;
  if(fanMesh.parent!==scene)scene.attach(fanMesh);
  fanMesh.position.copy(target.worldPosition);
  fanMesh.quaternion.copy(target.worldQuaternion);
  fanMesh.attachedToRadiatorUid=null;
  fanMesh.assignedSlotIndex=null;
  fanMesh.attachedToCaseUid=caseBox.uid;
  fanMesh.caseFanMountId=target.id;
  fanMesh.caseFanMountZone=target.zone;
  fanMesh.caseFanMountFace=target.face;
  fanMesh.caseFanMountSlot=Number(target.slot)||0;
  fanMesh.caseFanMountCount=Math.max(1,Number(target.count)||1);
  fanMesh.caseFanMountFrame=Number(target.frame)||0;
  fanMesh.caseFanEvidenceSources=[...(target.sources||[])];
  fanMesh.isLockedToCase=true;
  fanMesh.spatialUserAdjusted=false;
  fanMesh.activeFanSnapId=target.id;
  fanMesh.relationalOrientationSource='catalog_spatial_hint';
  fanMesh.relationalOrientationTrainingTier='verified_source_note';
  fanMesh.runtimeContainment={status:'placed',reason:'catalog_case_fan_mount'};
  fanMesh.updateMatrixWorld(true);
  caseBox.attach(fanMesh);
  fanMesh.updateMatrixWorld(true);
  fanMesh.wireframeHelper?.update?.();
  return true;
}

function nominalRadiatorGeometry(size) {
  const n=finite(size,0);
  if(n>=420)return {l:455,w:140,h:2};
  if(n>=360)return {l:397,w:120,h:2};
  if(n>=280)return {l:317,w:140,h:2};
  if(n>=240)return {l:277,w:120,h:2};
  if(n>=140)return {l:160,w:140,h:2};
  return {l:157,w:120,h:2};
}

function localRadiatorMarker(face,dims,rad) {
  const thickness=2;
  const base=localFacePosition(face,dims,thickness);
  const q=localFaceQuaternion(face);
  const orientation=radiatorOrientationForFace(face,dims,rad);
  if(!orientation)return null;
  const swapped=orientation==='swapped';
  // Build every marker in the same canonical XZ mounting plane, then rotate that plane onto
  // the requested chassis face. The old code created face-specific dimensions AND rotated
  // them, which double-transformed side/front markers and produced the long green planes
  // seen outside T1/NR200.
  const geometry=new THREE.BoxGeometry(swapped?rad.l:rad.w,thickness,swapped?rad.w:rad.l);
  return {base,q,geometry,orientation};
}

function lineMarkerFromGeometry(geometry,color,opacity=0.55) {
  const edges=new THREE.EdgesGeometry(geometry);
  geometry.dispose();
  const material=new THREE.LineBasicMaterial({color,transparent:true,opacity,depthTest:true});
  const lines=new THREE.LineSegments(edges,material);
  lines.userData.sffSpatialEvidenceMarker=true;
  lines.renderOrder=2;
  return lines;
}

export function renderCaseSpatialEvidenceMarkers(caseBox) {
  if(!caseBox?.isCase)return null;
  const prior=caseBox.getObjectByName?.('sff_case_spatial_evidence');
  if(prior){
    prior.parent?.remove(prior);
    prior.traverse?.(node=>{
      node.geometry?.dispose?.();
      const materials=node.material?(Array.isArray(node.material)?node.material:[node.material]):[];
      materials.forEach(material=>material?.dispose?.());
    });
  }
  const evidence=normalizeCatalogSpatialHints(caseBox.catalogRecord?.spatialHints);
  if(!evidence.fans.length&&!evidence.radiators.length)return null;
  const dims=caseDims(caseBox);
  const evidenceFrame=resolveSpatialEvidenceFrame(caseBox,evidence);
  const group=new THREE.Group();
  group.name='sff_case_spatial_evidence';
  group.userData.sffSpatialEvidence=true;
  group.userData.sffSpatialEvidenceFrame=evidenceFrame;

  for(const hint of evidence.fans){
    if(hint.snapEligible===false)continue;
    for(const size of hint.sizes||[]){
      const count=Math.max(0,Math.round(Number(hint.counts?.[String(Math.round(size))])||0));
      if(!count)continue;
      const markerThickness=1.5;
      const resolvedZone=resolveHardwareRelativeZone(resolvedEvidenceZone(hint.zone,evidenceFrame),caseBox,[]);
      if(!resolvedZone)continue;
      for(const face of zoneFaces(resolvedZone)){
        const axis=preferredRowAxis(face,dims,count,size);
        if(!axis)continue;
        const base=localFacePosition(face,dims,markerThickness);
        const q=localFaceQuaternion(face);
        for(const offset of rowOffsets(count,size)){
          const geometry=new THREE.BoxGeometry(size,markerThickness,size);
          const marker=lineMarkerFromGeometry(geometry,FAN_MARKER_COLOR,0.38);
          const local=base.clone();local[axis]=offset;
          marker.position.copy(local);marker.quaternion.copy(q);
          marker.userData={...marker.userData,type:'fan',zone:resolvedZone,sourceZone:hint.zone,evidenceFrame,face,size,sources:[...(hint.sources||[])]};
          group.add(marker);
        }
      }
    }
  }

  for(const hint of evidence.radiators){
    if(hint.snapEligible===false)continue;
    const size=Math.max(...(hint.sizes||[0]));
    if(!size)continue;
    const rad=nominalRadiatorGeometry(size);
    const resolvedZone=resolveHardwareRelativeZone(resolvedEvidenceZone(hint.zone,evidenceFrame),caseBox,[]);
    if(!resolvedZone)continue;
    for(const face of zoneFaces(resolvedZone)){
      if(!radiatorFitsFace(face,dims,rad))continue;
      const markerSpec=localRadiatorMarker(face,dims,rad);
      if(!markerSpec)continue;
      const marker=lineMarkerFromGeometry(markerSpec.geometry,RADIATOR_MARKER_COLOR,0.64);
      marker.position.copy(markerSpec.base);marker.quaternion.copy(markerSpec.q);
      marker.userData={
        ...marker.userData,
        type:'radiator',zone:resolvedZone,sourceZone:hint.zone,evidenceFrame,face,sizes:[...(hint.sizes||[])],counts:{...(hint.counts||{})},
        maxStack:hint.maxStack||null,maxRadiatorThickness:hint.maxRadiatorThickness||null,
        sources:[...(hint.sources||[])],conditions:[...(hint.conditions||[])]
      };
      group.add(marker);
    }
  }

  caseBox.add(group);
  caseBox.spatialEvidence=evidence;
  caseBox.spatialEvidenceFrame=evidenceFrame;
  return group;
}

function candidateZone(mount='', plan=null) {
  const raw=String(mount||'').toLowerCase();
  let zone='';
  if(raw.startsWith('top'))zone='top';
  else if(raw.startsWith('bottom'))zone='bottom';
  else if(raw.startsWith('front'))zone='front';
  else if(raw.startsWith('rear'))zone='rear';
  else if(raw.startsWith('side_'))zone='side';
  if(!zone)return '';

  // Candidate mount labels remain canonical after vertical-reference projection. Convert the
  // label back to the physical chassis face before matching it against catalogue note evidence.
  if(plan?.effective?.layoutFamily==='reference' && plan?.effective?.layoutTransform==='vertical'){
    const canonicalToPhysical={top:'rear',bottom:'front',front:'top',rear:'bottom'};
    zone=canonicalToPhysical[zone]||zone;
  }
  return zone;
}

export function annotateRadiatorCandidatesWithSpatialEvidence(plan, caseRecord, radiatorSize = 0, radiatorSpec = {}) {
  const evidence=normalizeCatalogSpatialHints(caseRecord?.spatialHints);
  const hints=evidence.radiators||[];
  if(!plan?.candidates?.radiator?.length||!hints.length)return plan;
  const evidenceFrame=resolveSpatialEvidenceFrame(caseRecord,evidence);
  const radThickness=finite(radiatorSpec?.radiatorThickness,0);
  const stackThickness=finite(radiatorSpec?.stackThickness,0);
  for(const candidate of plan.candidates.radiator){
    const zone=candidateZone(candidate.mount,plan);
    const matches=hints.filter(hint=>hint.snapEligible!==false && resolvedEvidenceZone(hint.zone,evidenceFrame)===zone && (!radiatorSize || hint.sizes.some(size=>Number(size)===Number(radiatorSize))));
    if(!matches.length)continue;
    // Prefer an unconditional statement over a conditional statement for the same source zone.
    const match=matches.find(hint=>!(hint.conditions||[]).length) || matches[0];
    const conditions=[...(match.conditions||[])];
    const conditional=conditions.length>0;
    const maxStack=finite(match.maxStack,0);
    const maxRadiatorThickness=finite(match.maxRadiatorThickness,0);
    const stackWithinLimit=!maxStack || !stackThickness || stackThickness<=maxStack+0.25;
    const radiatorWithinLimit=!maxRadiatorThickness || !radThickness || radThickness<=maxRadiatorThickness+0.25;
    const limitSatisfied=stackWithinLimit&&radiatorWithinLimit;
    const preferred=!conditional&&limitSatisfied;
    candidate.sourceSpatialEvidence={
      kind:'deterministic_note_parse',
      zone:resolvedEvidenceZone(match.zone,evidenceFrame),
      sourceZone:match.zone,
      evidenceFrame,
      sizes:[...match.sizes],
      counts:{...(match.counts||{})},
      maxStack:maxStack||null,
      maxRadiatorThickness:maxRadiatorThickness||null,
      sources:[...(match.sources||[])],
      conditions,
      conditional,
      limitSatisfied,
      preferred
    };
    // Source evidence is a ranking input, not a hard whitelist. Conditional statements and
    // explicit thickness limits that this selected AIO exceeds stay visible as evidence but
    // do not outrank the established hard-valid fallback candidates.
    if(preferred)candidate.priority=(Number(candidate.priority)||0)+40;
  }
  plan.candidates.radiator.sort((a,b)=>(Number(b.priority)||0)-(Number(a.priority)||0));
  return plan;
}
